/** @file Transform mapping an item handle axis onto a socket grip axis (used by compiler and runtime equip). */
import { trig } from '@pose2d/math/trig';
import type { Affine } from '@pose2d/math/types';
import type { Handle } from '@pose2d/format/types';

/**
 * out = inverse(H) (handle frame in attachment space, y flipped), scaled by socketLength/|hb-ha| when fitLength.
 * H has its origin at a' = (ax, -ay) and its X axis toward b' = (bx, -by); out maps that y-flipped handle space
 * onto the socket bone (a -> origin, b -> +X). A degenerate handle (a = b) keeps rotation 0 and scale 1.
 */
export function computeMountTransform(out: Affine, handle: Handle, socketLength: number, fitLength: boolean): Affine {
  const ax = handle.a[0];
  const ay = -handle.a[1];
  const dx = handle.b[0] - ax;
  const dy = -handle.b[1] - ay;
  const len = trig.hypot(dx, dy);
  const cos = len > 0 ? dx / len : 1;
  const sin = len > 0 ? dy / len : 0;
  const s = fitLength && len > 0 ? socketLength / len : 1;
  out[0] = s * cos;
  out[1] = s * sin;
  out[2] = -s * sin;
  out[3] = s * cos;
  out[4] = -s * (cos * ax + sin * ay);
  out[5] = -s * (-sin * ax + cos * ay);
  return out;
}
